import { isSchedulingFieldType, type IntakeFormField } from "@/schema"

const numberFormatter = new Intl.NumberFormat()

function optionLabel(field: IntakeFormField, value: unknown) {
  const option = field.options?.find((candidate) => String(candidate.value) === String(value))
  return option?.label ?? String(value)
}

function formatObject(value: object): string {
  return Object.entries(value)
    .filter(([, nested]) => nested !== null && nested !== undefined && nested !== "")
    .map(([key, nested]) => `${key.replaceAll("_", " ")}: ${formatAnswer(nested)}`)
    .join(" · ")
}

function formatAddress(value: object): string {
  const address = value as Record<string, unknown>
  const locality = [address.city, address.state].filter(Boolean).join(", ")
  const localityWithPostalCode = [locality, address.zip || address.postal_code].filter(Boolean).join(" ")

  return [address.street1, address.street2, localityWithPostalCode, address.country]
    .filter(Boolean)
    .map(String)
    .join("\n")
}

function formatCalendly(value: object): string {
  const answer = value as Record<string, unknown>
  if (typeof answer.start_time !== "string") return formatObject(value)

  const startTime = new Date(answer.start_time)
  if (Number.isNaN(startTime.valueOf())) return formatObject(value)

  const timezone = typeof answer.timezone === "string" ? answer.timezone : "UTC"
  try {
    const scheduledTime = new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: timezone,
    }).format(startTime)
    return `${scheduledTime} (${timezone})`
  } catch {
    return `${startTime.toISOString()} (UTC)`
  }
}

function formatRepeaterRow(value: unknown, field: IntakeFormField): string {
  if (!value || typeof value !== "object" || Array.isArray(value)) return formatAnswer(value)

  const row = value as Record<string, unknown>
  const subFields = field.item_config?.fields ?? []
  return subFields
    .filter(
      (subField) =>
        subField.type !== "file" &&
        row[subField.key] !== null &&
        row[subField.key] !== undefined &&
        row[subField.key] !== ""
    )
    .map((subField) => `${subField.label}: ${formatAnswer(row[subField.key], subField)}`)
    .join(" · ")
}

export function formatAnswer(value: unknown, field?: IntakeFormField): string {
  if (value === null || value === undefined || value === "") return "Not answered"
  if (typeof value === "boolean") return value ? "Yes" : "No"

  if (Array.isArray(value)) {
    if (value.length === 0) return "Not answered"
    if (field?.type === "multi_select") return value.map((item) => optionLabel(field, item)).join(", ")
    if (field?.type === "repeater") {
      return value.map((item, index) => `${index + 1}. ${formatRepeaterRow(item, field)}`).join("\n")
    }
    return value.map((item) => formatAnswer(item)).join(", ")
  }

  if (typeof value === "object") {
    if (field?.type === "address") return formatAddress(value)
    if (field && isSchedulingFieldType(field.type)) return formatCalendly(value)
    return formatObject(value)
  }
  if (field?.type === "select" || field?.type === "radio") return optionLabel(field, value)

  if (field?.type === "currency") {
    const amount = Number(value)
    if (Number.isFinite(amount)) {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: field.currency ?? "USD",
        maximumFractionDigits: field.decimals ?? 2,
      }).format(amount)
    }
  }

  if (field?.type === "percent") return `${value}%`
  if (field?.type === "number" && typeof value === "number") return numberFormatter.format(value)

  if (field?.type === "date" && typeof value === "string") {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
    if (match) {
      const localDate = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
      if (!Number.isNaN(localDate.valueOf())) {
        return new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(localDate)
      }
    }
  }

  return String(value)
}

export function formatByteSize(bytes: number): string {
  if (bytes < 1_024) return `${bytes} B`

  const units = ["KB", "MB", "GB"]
  let size = bytes / 1_024
  let unitIndex = 0
  while (size >= 1_024 && unitIndex < units.length - 1) {
    size /= 1_024
    unitIndex += 1
  }
  return `${new Intl.NumberFormat(undefined, { maximumFractionDigits: 1 }).format(size)} ${units[unitIndex]}`
}
